/**
 * The form furniture the settings screens share.
 *
 * These exist so the four screens cannot describe the same thing four ways —
 * one switch, one segmented control, one confirmation shape. Nothing here holds
 * domain knowledge; the rules live in `logic.ts`.
 */

import { useId, useState } from "react";
import type { ReactNode } from "react";

import { Link } from "../../main.tsx";
import type { ChoiceCopy } from "./logic.ts";

import "./settings.css";

export function SettingsScreen(props: {
  title: string;
  subtitle?: ReactNode;
  /** Route to go back to. Settings sub-screens always come from the hub. */
  back?: { to: string; label: string };
  children: ReactNode;
}): ReactNode {
  const { title, subtitle, back, children } = props;
  return (
    <div className="pb-set">
      <header className="pb-set__head">
        {back && (
          <Link to={back.to} className="pb-set__back">
            <span aria-hidden="true">←</span> {back.label}
          </Link>
        )}
        <h1 className="pb-set__title">{title}</h1>
        {subtitle !== undefined && <p className="pb-set__sub">{subtitle}</p>}
      </header>
      {children}
    </div>
  );
}

export function Section(props: {
  title: string;
  aside?: ReactNode;
  hint?: ReactNode;
  children: ReactNode;
}): ReactNode {
  const { title, aside, hint, children } = props;
  return (
    <section className="pb-sec">
      <div className="pb-sec__head">
        <h2>{title}</h2>
        {aside}
      </div>
      {hint !== undefined && <p className="pb-sec__hint">{hint}</p>}
      {children}
    </section>
  );
}

export function Row(props: { label: ReactNode; hint?: ReactNode; children: ReactNode }): ReactNode {
  return (
    <div className="pb-row">
      <span className="pb-row__label">
        <span className="pb-label">{props.label}</span>
        {props.hint !== undefined && <span className="pb-row__hint">{props.hint}</span>}
      </span>
      {props.children}
    </div>
  );
}

export function Note({ children }: { children: ReactNode }): ReactNode {
  return <p className="pb-note">{children}</p>;
}

export function Banner(props: {
  tone: "info" | "warn" | "danger" | "ok";
  title: string;
  children?: ReactNode;
  actions?: ReactNode;
}): ReactNode {
  return (
    <div className="pb-banner" data-tone={props.tone}>
      <span className="pb-banner__title">{props.title}</span>
      {props.children !== undefined && <p className="pb-banner__body">{props.children}</p>}
      {props.actions !== undefined && <div className="pb-actions">{props.actions}</div>}
    </div>
  );
}

export function Problems({ items }: { items: readonly string[] }): ReactNode {
  if (items.length === 0) return null;
  return (
    <ul className="pb-problems">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

export function TextField(props: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  hint?: ReactNode;
  invalid?: boolean;
  autoFocus?: boolean;
}): ReactNode {
  const id = useId();
  return (
    <label className="pb-field" htmlFor={id}>
      <span className="pb-field__label">{props.label}</span>
      <input
        id={id}
        className="pb-input"
        type="text"
        value={props.value}
        placeholder={props.placeholder}
        data-invalid={props.invalid ? "true" : undefined}
        aria-invalid={props.invalid ? "true" : undefined}
        autoFocus={props.autoFocus}
        onChange={(e) => props.onChange(e.currentTarget.value)}
      />
      {props.hint !== undefined && <span className="pb-row__hint">{props.hint}</span>}
    </label>
  );
}

export interface SegmentOption<T extends string> {
  value: T;
  copy: ChoiceCopy;
}

/**
 * A choice plus, always, the sentence explaining what the selected option
 * actually does. `kind` and `polarity` decide whether a number may ever be
 * averaged; that is not something to leave to a label two words long.
 */
export function Segmented<T extends string>(props: {
  label: string;
  value: T;
  options: readonly SegmentOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
}): ReactNode {
  const selected = props.options.find((o) => o.value === props.value);
  return (
    <div className="pb-field">
      <span className="pb-field__label">{props.label}</span>
      <div className="pb-seg" role="group" aria-label={props.label}>
        {props.options.map((option) => (
          <button
            key={option.value}
            type="button"
            className="pb-seg__opt"
            aria-pressed={option.value === props.value}
            disabled={props.disabled}
            onClick={() => props.onChange(option.value)}
          >
            {option.copy.title}
          </button>
        ))}
      </div>
      {selected && <span className="pb-seg__line">{selected.copy.line}</span>}
    </div>
  );
}

export function Switch(props: {
  label: ReactNode;
  hint?: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}): ReactNode {
  return (
    <label className="pb-switch">
      <input
        type="checkbox"
        checked={props.checked}
        disabled={props.disabled}
        onChange={(e) => props.onChange(e.currentTarget.checked)}
      />
      <span className="pb-switch__box" aria-hidden="true" />
      <span className="pb-row__label">
        <span className="pb-label">{props.label}</span>
        {props.hint !== undefined && <span className="pb-row__hint">{props.hint}</span>}
      </span>
    </label>
  );
}

/**
 * A destructive action that has to say what it destroys before it will run.
 *
 * The confirmation lists specifics rather than asking "are you sure?", and the
 * really destructive variant makes you type a word, because a second button in
 * the same place as the first is just a slower first button.
 */
export function ConfirmButton(props: {
  label: string;
  title: string;
  consequences: readonly string[];
  confirmLabel: string;
  onConfirm: () => void | Promise<void>;
  /** When set, the confirm button stays disabled until this is typed exactly. */
  phrase?: string;
  disabled?: boolean;
  disabledReason?: string | null;
  variant?: "quiet" | "danger";
  /**
   * Collapse the trigger to a shape the caller places itself — the × in a night
   * card's corner is the one that needed this. Set together or not at all: a
   * class without a name is a mystery square, and a name without a class is a
   * full-width button somewhere it was not meant to be, so they are one prop.
   *
   * `label` is still required and still the wording of the action; `glyph` is
   * only what is drawn. When this is set, `variant` no longer applies — the
   * caller's class is the whole appearance — and the trigger is emitted without
   * the settings-row span, because the caller is doing the layout.
   *
   * The expanded confirmation is deliberately not themeable either way: whatever
   * opened it, the thing being confirmed is destructive and reads the same.
   */
  icon?: { glyph: string; className: string };
}): ReactNode {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const ready = props.phrase === undefined || typed.trim().toUpperCase() === props.phrase;

  if (!open) {
    const icon = props.icon;
    const trigger = (
      <button
        type="button"
        className={
          icon ? icon.className : props.variant === "danger" ? "pb-btn pb-danger" : "pb-btn"
        }
        data-variant={icon || props.variant === "danger" ? undefined : "quiet"}
        // The glyph carries no name, so the label has to be said out loud —
        // and `title` says it to a mouse as well as to a screen reader.
        aria-label={icon ? props.label : undefined}
        title={icon ? props.label : undefined}
        disabled={props.disabled}
        onClick={() => setOpen(true)}
      >
        {icon ? icon.glyph : props.label}
      </button>
    );
    const reason =
      props.disabled && props.disabledReason ? (
        <span className="pb-row__hint">{props.disabledReason}</span>
      ) : null;

    // No settings-row span around an icon trigger: it is placed by whoever asked
    // for it, and a wrapper here would be a box that caller has to undo.
    return icon ? (
      <>
        {trigger}
        {reason}
      </>
    ) : (
      <span className="pb-row__label">
        {trigger}
        {reason}
      </span>
    );
  }

  return (
    <div className="pb-banner" data-tone="danger">
      <span className="pb-banner__title">{props.title}</span>
      <ul className="pb-danger__list">
        {props.consequences.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      {props.phrase !== undefined && (
        <TextField
          label={`Type ${props.phrase} to confirm`}
          value={typed}
          onChange={setTyped}
          placeholder={props.phrase}
          autoFocus
        />
      )}
      <div className="pb-actions">
        <button
          type="button"
          className="pb-btn pb-danger"
          data-variant="primary"
          disabled={!ready || busy}
          onClick={async () => {
            setBusy(true);
            try {
              await props.onConfirm();
              setOpen(false);
              setTyped("");
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? "Working…" : props.confirmLabel}
        </button>
        <button
          type="button"
          className="pb-btn"
          data-variant="quiet"
          onClick={() => {
            setOpen(false);
            setTyped("");
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }): ReactNode {
  return <p className="pb-empty">{children}</p>;
}

export function Dot({ colour, title }: { colour: string; title?: string }): ReactNode {
  return <span className="pb-dot" style={{ background: colour }} title={title} aria-hidden="true" />;
}
